import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { NgbActiveModal, NgbDateStruct, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { RechercherPrestataireComponent } from '../rechercher-prestataire/rechercher-prestataire.component';
import { debounceTime, distinctUntilChanged, startWith, takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { NavigationContext, SessionContext } from 'src/app/core/services/config/app.settings';
import { Produit } from 'src/app/data/habilitation/models/produit.model';
import { TypeVin } from 'src/app/data/declaration/models/type-vin.model';
import { UtilisateurTypeCode } from 'src/app/data/intervenant/models/enums/type-utilisateur.enum';
import { StockProduit } from 'src/app/data/declaration/models/stock-produit.model';
import { OperateurSitesService } from 'src/app/data/declaration/services/operateur-sites/operateur-sites.service';
import { StockProduitService } from 'src/app/data/declaration/services/stock-produit/stock-produit.service';
import { ReferentielService } from 'src/app/data/declaration/services/referentiel/referentiel.service';
import { Referentiel } from 'src/app/data/declaration/models/referentiel.model';
import { Site } from 'src/app/data/declaration/models/site.model';
import { forkJoin, Subject } from 'rxjs';
import { TypeCahierCode } from 'src/app/data/habilitation/models/enums/type-cahier-code.enum';
import { ProduitsService } from 'src/app/data/declaration/services/produits/produits.service';
import { SaisieTirage } from 'src/app/data/declaration/models/saisie-tirage.model';
import { Operateur } from 'src/app/data/intervenant/models/operateur.model';
import { Assemblage } from 'src/app/data/declaration/models/assemblage.model';
import { MouvementsProduitsService } from 'src/app/data/declaration/services/mouvements/mouvements-produits.service';
import { ToastrService } from 'ngx-toastr';
import moment from 'moment';
import { Contenant } from 'src/app/data/declaration/models/contenant.model';
import { TypeChangementProduitCode } from 'src/app/data/declaration/models/enums/type-changement-produit-code.enum';
import { LoaderService } from '../../../../../../../core/services/loader/loader.service';

@Component({
    selector: 'app-saisir-tirage',
    templateUrl: './saisir-tirage.component.html',
    styleUrls: ['./saisir-tirage.component.scss'],
    standalone: false
})
export class SaisirTirageComponent implements OnInit, OnDestroy  {
    @Input()
    syntheseProduit: Produit;
    @Input()
    campagne: string;
    @Input()
    typeCahier: TypeCahierCode;
    @Input()
    estCreation = true;
    @Input()
    tirageAModifier: SaisieTirage;

    public formSaisieTirage: UntypedFormGroup;

    millesimeActuel: number | 'Non millésimé' = 'Non millésimé';

    private readonly destroy$ = new Subject<void>();

    get form_produits() {
        return this.formSaisieTirage.get('produits');
    }

    get numeroTirage() {
        return this.formSaisieTirage.get('numeroTirage');
    }

    get date() {
        return this.formSaisieTirage.get('date');
    }
    get lot() {
        return this.formSaisieTirage.get('lot');
    }
    get volume() {
        return this.formSaisieTirage.get('volume');
    }
    get volumeLiqueur() {
        return this.formSaisieTirage.get('volumeLiqueur');
    }
    get entreposage() {
        return this.formSaisieTirage.get('entreposage');
    }

    get typeVin() {
        return this.formSaisieTirage.get('typeVin');
    }

    idOperateur: number;
    loaded = false;
    produits: Produit[];

    public referentiel: Referentiel = new Referentiel();
    public sitesOperateur: Site[] = [];
    public stocksProduit: StockProduit[] = [];
    // public syntheseProduit: Produit = new Produit();
    public stockCourant: StockProduit = Object.assign(new StockProduit(), {
        stock: 0
    });
    public typesVin: TypeVin[] = [];

    get volumeTotalValue() {
        let volumeTotal = 0;

        // Récupérer le volume de l'année en cours
        if (this.volume.value) {
            volumeTotal = this.volume.value;
        }

        // Additionner les volumes des assemblages
        (this.formSaisieTirage.get('assemblages') as UntypedFormArray).controls.forEach(assemblage => {
            const volumeAssemblage = assemblage.get('volume').value;
            if (volumeAssemblage) {
                volumeTotal += volumeAssemblage;
            }
        });
        // additionner volume liqueur
        if (this.volumeLiqueur.value) {
            volumeTotal += this.volumeLiqueur.value;
        }

        return this.bypassFloatingPointPrecision(volumeTotal);
    }

    getMillesimeActuel(): number | 'Non millésimé' {
        const assemblages: Assemblage[] = this.getAssemblages().controls.map(control =>
            Object.assign(new Assemblage(), {
                annee: control.get('annee')?.value?.annee,
                volume: Number(control.get('volume')?.value) || 0
            })
        );

        // Ajout du millésime actuellement saisi
        assemblages.push(
            Object.assign(new Assemblage(), {
                annee: this.campagne,
                volume: Number(this.volume.value) || 0
            })
        );

        const volumeTotal = assemblages.reduce(
            (total, assemblage) => total + assemblage.volume,
            0
        );

        if (volumeTotal === 0) {
            return 'Non millésimé';
        }

        // Regroupe les volumes si un même millésime apparaît plusieurs fois
        const volumesParMillesime = new Map<number, number>();

        assemblages.forEach(assemblage => {
            volumesParMillesime.set(
                assemblage.annee,
                (volumesParMillesime.get(assemblage.annee) || 0) + assemblage.volume
            );
        });

        let millesimeMajoritaire: number;
        let volumeMajoritaire = 0;

        volumesParMillesime.forEach((volume, annee) => {
            if (volume > volumeMajoritaire) {
                volumeMajoritaire = volume;
                millesimeMajoritaire = annee;
            }
        });

        const pourcentage = (volumeMajoritaire / volumeTotal) * 100;

        return pourcentage >= 85 ? millesimeMajoritaire : 'Non millésimé';
    }

    constructor(
        public readonly modal: NgbActiveModal,
        private readonly modalService: NgbModal,
        private readonly fb: UntypedFormBuilder,
        private readonly translate: TranslateService,
        private readonly operateurSitesService: OperateurSitesService,
        private readonly stockProduitService: StockProduitService,
        private readonly referentielService: ReferentielService,
        private readonly loaderService: LoaderService,
        private readonly produitServiceDec: ProduitsService,
        private readonly mouvementsProduitsService: MouvementsProduitsService,
        private readonly toastr: ToastrService
    ) {}

    ngOnInit() {
        if (this.estCreation) {
            this.idOperateur =
                SessionContext.get('utilisateurType') === UtilisateurTypeCode.OPERATEUR
                    ? SessionContext.get('idIntervenant')
                    : NavigationContext.get('idOperateur');
        } else {
            this.idOperateur = this.tirageAModifier.idOperateur;
        }

        this.formSaisieTirage = this.fb.group({
            // produit: [{ value: this.syntheseProduit.libelle, disabled: true }],
            date: [undefined, Validators.required],
            lot: [undefined, Validators.required],
            numeroTirage: [undefined, Validators.required],
            volume: [
                undefined,
                [Validators.pattern(/(^\d+$)|(^\d+\.\d{1,2}$)|(^\d+\,\d{1,2}$)/), this.positifStrictValidator, this.volumeCourantValidator.bind(this)]
            ],
            volumeLiqueur: [
                undefined,
                [Validators.pattern(/(^\d+$)|(^\d+\.\d{1,2}$)|(^\d+\,\d{1,2}$)/), this.positifStrictValidator, this.volumeCourantValidator.bind(this)]
            ],
            assemblages: this.fb.array([]),
            entreposage: [undefined, Validators.required],
            contenants: this.fb.array([], Validators.compose([this.contenantsValidator.bind(this)])),
            typeVin: [undefined, Validators.required],
            produits: [undefined, Validators.required],
            prestataire: undefined
        });

        this.formSaisieTirage.get('volume').valueChanges.subscribe(() => {
            const contenants = this.formSaisieTirage.get('contenants') as UntypedFormArray;
            contenants.controls.forEach((contenant: UntypedFormGroup) => contenant.get('volume').updateValueAndValidity());
        });

         this.formSaisieTirage.valueChanges
            .pipe(
                startWith(this.formSaisieTirage.value),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                this.millesimeActuel = this.getMillesimeActuel();
            });

        this.loadData();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }


    close() {
        this.modal.dismiss();
    }

    submit() {
        if (this.formSaisieTirage.valid) {
            const date = this.getField('date').value as NgbDateStruct;

            const saisie = new SaisieTirage();
            saisie.codeProduit = (this.getField('produits').value as Produit).code;
            saisie.codeProduitBase = this.syntheseProduit.code;
            saisie.dateTirage = moment([date.year, date.month - 1, date.day, 0, 0, 0]);
            saisie.numeroTirage = this.getField('numeroTirage').value;
            saisie.numeroLot = this.getField('lot').value;
            saisie.volumeVinBase = this.volume.value;
            saisie.volumeLiqueur = this.volumeLiqueur.value;
            saisie.site = this.getField('entreposage').value;
            saisie.typeVin = this.getField('typeVin').value;

            const prestataire = this.getField('prestataire').value as Operateur;
            if (prestataire) {
                saisie.idPrestataire = prestataire.id;
            }

            // assemblages
            saisie.assemblages = []; 
            console.log("ASSEMBLAGES ",this.getAssemblages().controls);
            this.getAssemblages().controls.forEach(assemblage => {
                saisie.assemblages.push(
                    Object.assign(new Assemblage(), {
                        annee: assemblage.get('annee').value.annee,
                        volume: assemblage.get('volume').value
                    })
                );
            });

            // Récupérer les contenants
            (this.getField('contenants') as UntypedFormArray).controls.forEach(contenant => {
                saisie.contenants.push(
                    Object.assign(new Contenant(), {
                        idType: contenant.get('type').value.idContenant,
                        nombre: contenant.get('nombre').value,
                        volume: contenant.get('volume').value
                    })
                );
            });

             if (this.estCreation) {
                // Envoyer le conditionnement à l'API
                this.mouvementsProduitsService.creerTirage(this.idOperateur, this.campagne, saisie).subscribe(() => {
                    this.translate.get('page.declarations.synthese.modal.saisirTirage.creation-ok').subscribe(msg => {
                        this.toastr.success('', msg);
                    });
                    this.modal.close();
                });
            } else {
                saisie.assemblages.push(
                    Object.assign(new Assemblage(), {
                        annee: this.campagne,
                        volume: this.volume.value
                    })
                );
                saisie.idMouvement = this.tirageAModifier.idMouvement;
                this.mouvementsProduitsService.majTirage(this.idOperateur,saisie).subscribe(() => {
                    this.translate.get('page.declarations.synthese.modal.saisirTirage.modification-ok').subscribe(msg => {
                        this.toastr.success('', msg);
                    });
                    this.modal.close();
                });
            }
            
        }
    }

    loadData() {
        if (this.syntheseProduit) {
            // Charger les stocks, les contenants, les adresses, les produits liés
            forkJoin([
                this.stockProduitService.getStockByProductCodeAndMoveType(this.idOperateur, this.syntheseProduit.code, 'VRAC'),
                this.referentielService.getReferentiel(),
                this.operateurSitesService.getSitesOperateur(this.idOperateur),
                this.produitServiceDec.getProduitsByProduitOrigineAndTypeChangement(this.syntheseProduit.id, TypeChangementProduitCode.TIRAGE)
            ])
                .pipe(this.loaderService.operator())
                .subscribe(([stocks, ref, operateurs, produitsByProduitOrigine]) => {
                    // Affecter les stocks
                    
                    stocks.forEach(stock => {
                        if (stock.annee.toString() === this.campagne) {
                            this.stockCourant = stock;
                        } else {
                            this.stocksProduit.push(stock);
                        }
                    });
                    // Affecter les contenants
                    this.referentiel = ref;
                    // Affecter les adresses
                    this.sitesOperateur = operateurs;
                    // Récupérer les produits liés au CDC sélectionné
                    this.produits = produitsByProduitOrigine;
                    this.form_produits.enable();

                    if (!this.estCreation) {
                        this.loadTirage();
                    }

                    this.loaded = true;
                });
        }
    }

    loadTirage() {
        this.date.setValue({
            year: this.tirageAModifier.dateTirage.year(),
            month: this.tirageAModifier.dateTirage.month() + 1,
            day: this.tirageAModifier.dateTirage.date()
        });

        this.numeroTirage.setValue(this.tirageAModifier.numeroTirage);
        this.lot.setValue(this.tirageAModifier.numeroLot);
        this.volume.setValue(this.tirageAModifier.volumeVinBase);
        this.volumeLiqueur.setValue(this.tirageAModifier.volumeLiqueur);

        this.entreposage.setValue(this.sitesOperateur.find(s => s.id === this.tirageAModifier.site.id));
        this.form_produits.setValue(this.produits.find(p => p.id === this.tirageAModifier.produit.id));

        this.typeVin.setValue(this.referentiel.refMvtTypesVin.find(p => p.id === this.tirageAModifier.typeVin.id));
        
        const assemblageMax = this.tirageAModifier.assemblages.reduce(
            (max, cond) => !max || cond.volume > max.volume ? cond : max,
            null
            );


        
        this.volume.setValue(this.tirageAModifier.volumeVinBase);

        const annee = assemblageMax?.annee;
        
        this.campagne = annee.toString();

        const assemblages = this.formSaisieTirage.get('assemblages') as UntypedFormArray;

        this.tirageAModifier.assemblages
            .filter(ass => ass.annee !== annee)
            .forEach(assemblage => {
                let form: UntypedFormGroup = null;

                let stockProduit: StockProduit;

                stockProduit = this.stocksProduit.find(stock => Number(stock.annee) === Number(assemblage.annee)) ;

                stockProduit.stock += assemblage.volume;               

                form = this.fb.group({
                    annee: [ 
                        this.stocksProduit.find(stock => Number(stock.annee) === Number(assemblage.annee)),
                        [this.selectedYearsOrContenantValidator('assemblages', 'annee').bind(this)]
                    ],
                    volume: [
                        assemblage.volume,
                        [
                            Validators.pattern(/(^\d+$)|(^\d+\.\d{1,2}$)|(^\d+\,\d{1,2}$)/),
                            this.positifStrictValidator,
                            this.assemblageVolumeValidator,
                            Validators.required
                        ]
                    ]
                });

                assemblages.push(form);
            });

        this.stockCourant = this.stocksProduit.find(s => s.annee === annee);
        this.stockCourant.stock += this.volume.value;

        const contenants = this.formSaisieTirage.get('contenants') as UntypedFormArray;
        this.tirageAModifier.contenants.forEach(contenant => {
            let form: UntypedFormGroup = null;

            form = this.fb.group({
                type: [
                    this.referentiel.refContenantList.find(cont => cont.idContenant === contenant.idType),
                    [this.selectedYearsOrContenantValidator('contenants', 'type').bind(this)]
                ],
                nombre: [contenant.nombre, [Validators.pattern(/^[0-9]\d*$/), this.positifStrictValidator]],
                volume: [
                    contenant.volume,
                    [
                        Validators.pattern(/(^\d+$)|(^\d+\.\d{1,3}$)|(^\d+\,\d{1,3}$)/),
                        this.positifStrictValidator,
                        this.contenantValidator.bind(this),
                        Validators.required
                    ]
                ]
            });

            contenants.push(form);
        });
    }

    rechercherPrestataire() {
        const modal = this.modalService.open(RechercherPrestataireComponent, {
            backdrop: 'static',
            size: 'lg'
        });
        modal.componentInstance.idOperateur = this.idOperateur;

        modal.result.then(
            acheteur => {
                this.formSaisieTirage.get('prestataire').setValue(acheteur);
            },
            () => {}
        );
    }

    get raisonSocialeAcheteur() {
        if (this.formSaisieTirage.get('prestataire').value) {
            return this.formSaisieTirage.get('prestataire').value.raisonSociale || this.formSaisieTirage.get('prestataire').value.email;
        }
        return '';
    }

    getContenants() {
        return (this.formSaisieTirage.get('contenants') as UntypedFormArray).controls;
    }

    addContenant(type: string) {
        const control = this.formSaisieTirage.get(type) as UntypedFormArray;
        control.push(this.init(type));
    }

    volumeLteStockValidatorFactory(stockType: 'courant' | 'assemblage') {
        return (control: UntypedFormControl) => {
            let stock = null;
            try {
                if (stockType === 'courant') {
                    stock = this.stockCourant.stock;
                } else {
                    stock = control.parent.get('annee').value.stock;
                }
            } catch (e) {
                stock = 0;
            }
            return /* this.typeCahier !== 'IGP' &&*/ control.value > stock ? { invalid: true } : null;
        };
    }

    assemblageVolumeValidator(control: UntypedFormControl) {
        let result = null;

        if (control.value && control.parent) {
            result = control.value > control.parent.get('annee').value.stock ? { invalid: true } : null;
        }

        return result;
    }

    getField(key: string) {
        return this.formSaisieTirage.get(key);
    }

    volumeCourantValidator(control: UntypedFormControl) {
        return control.value > this.stockCourant.stock ? { invalid: true } : null;
    }
    contenantsValidator() {
        let result = null;
        let volumes = 0;
        let volumeTotal = 0;
        const arrayVolumes: AbstractControl[] = [];

        if (this.formSaisieTirage) {
            const form = this.formSaisieTirage.get('contenants') as UntypedFormArray;
            volumeTotal = this.volumeTotalValue;
            form.controls.forEach(control => {
                volumes += +control.value.volume;
                arrayVolumes.push(control);
            });
        }

        if (volumes > volumeTotal) {
            result = { invalid: true };
            arrayVolumes.forEach(control => {
                const errors = control.get('volume').errors || {};
                errors.incorrect = true;
                control.get('volume').setErrors(errors);
            });
        } else {
            arrayVolumes.forEach(control => {
                let errors = control.get('volume').errors || {};
                delete errors.incorrect;
                if (Object.keys(errors).length === 0) {
                    errors = null;
                }
                control.get('volume').setErrors(errors);
            });
        }

        return result;
    }

    remove(type: string, index: number) {
        const control = this.formSaisieTirage.get(type) as UntypedFormArray;
        control.removeAt(index);
    }

    init(type: string): UntypedFormGroup {
        let form: UntypedFormGroup = null;

        if (type === 'assemblages') {
            form = this.fb.group({
                annee: [undefined, [this.selectedYearsOrContenantValidator(type, 'annee').bind(this)]],
                volume: [
                    undefined,
                    [
                        Validators.pattern(/(^\d+$)|(^\d+\.\d{1,2}$)|(^\d+\,\d{1,2}$)/),
                        this.positifStrictValidator,
                        this.assemblageVolumeValidator,
                        Validators.required
                    ]
                ]
            });
        } else if (type === 'contenants') {
            form = this.fb.group({
                type: [undefined, [this.selectedYearsOrContenantValidator(type, 'type').bind(this)]],
                nombre: [undefined, [Validators.pattern(/^[0-9]\d*$/), this.positifStrictValidator]],
                volume: [
                    undefined,
                    [
                        Validators.pattern(/(^\d+$)|(^\d+\.\d{1,2}$)|(^\d+\,\d{1,2}$)/),
                        this.positifStrictValidator,
                        this.contenantValidator.bind(this),
                        Validators.required
                    ]
                ]
            });

            // Calculer automatiquement le volume lorsque l'on modifie le nombre
            form.get('nombre')
                .valueChanges.pipe(debounceTime(500), distinctUntilChanged())
                .subscribe(value => this.calculerVolumeContenant(value, form));

            // Calculer automatiquement le nombre lorsque l'on modifie le volume
            form.get('volume')
                .valueChanges.pipe(debounceTime(500), distinctUntilChanged())
                .subscribe(value => this.calculerNombreContenant(value, form));
        }

        return form;
    }

    selectedYearsOrContenantValidator(formulaire: string, champ: string): ValidatorFn {
        return (control: UntypedFormControl): any => {
            const value = control.value;

            if (!value || !this.formSaisieTirage) {
                return null;
            }

            const formArray = this.formSaisieTirage.get(formulaire) as UntypedFormArray;

            if (!formArray) {
                return null;
            }

            const valeurCourante = this.getComparableValue(value);

            let trouve = 0;

            formArray.controls.forEach(group => {
                const autreValue = group.get(champ)?.value;

                if (autreValue) {
                    const autreValeurComparable = this.getComparableValue(autreValue);

                    if (autreValeurComparable === valeurCourante) {
                        trouve++;
                    }
                }
            });

            return trouve >= 2 ? { invalid: true } : null;
        };
    }

    private getComparableValue(value: any): any {
        if (value?.id) {
            return value.id;
        }

        if (value?.annee) {
            return Number(value.annee);
        }

        return value;
    }

    calculerVolumeContenant(value: number, form: UntypedFormGroup) {
        value = Math.floor(value);
        form.patchValue({ nombre: value });

        if (value > 0) {
            const type = form.get('type').value;
            if (type) {
                form.patchValue({
                    volume: this.bypassFloatingPointPrecision(type.volumeContenant * value)
                });
            }
        }
    }

    calculerNombreContenant(value: number, form: UntypedFormGroup) {
        if (value > 0) {
            const type = form.get('type').value;
            if (type) {
                const nombre = Math.floor(this.bypassFloatingPointPrecision(value / type.volumeContenant));

                form.patchValue({
                    nombre: nombre.toString(),
                    volume: this.bypassFloatingPointPrecision(nombre * type.volumeContenant)
                });
            }
        }
    }

    positifStrictValidator(control: UntypedFormControl) {
        const value = parseFloat(control.value);

        if (isNaN(value) && control.value) {
            return { invalid: true };
        }
        return control.value > 0 ? null : { invalid: true };
    }

    contenantValidator(control: UntypedFormControl) {
        return control.value > this.volumeTotalValue ? { invalid: true } : null;
    }

    private bypassFloatingPointPrecision(nombre: number): number {
        return Math.round(nombre * 1000) / 1000;
    }

    createFormAssemblage() {
        return this.fb.group({
            annee: [undefined, [this.uniqueValueInFormArrayValidatorFactory(this.getAssemblages(), 'annee'), Validators.required]],
            volume: [
                undefined,
                [
                    Validators.pattern(/(^\d+$)|(^\d+\.\d{1,2}$)|(^\d+\,\d{1,2}$)/),
                    this.positifStrictValidator,
                    this.volumeLteStockValidatorFactory('assemblage'),
                    Validators.required
                ]
            ]
        });
    }

    getAssemblages() {
        return this.formSaisieTirage.get('assemblages') as UntypedFormArray;
    }
    getAssemblage(index: number) {
        return this.formSaisieTirage.get('assemblages.' + index) as UntypedFormGroup;
    }

    addAssemblage() {
        this.getAssemblages().push(this.createFormAssemblage());
    }

    removeAssemblage(index: number) {
        this.getAssemblages().removeAt(index);
    }

    uniqueValueInFormArrayValidatorFactory(formArray: UntypedFormArray, fieldKey: string) {
        return (control: UntypedFormControl) => {
            formArray.controls.forEach((group: UntypedFormGroup) => {
                const ct = group.get(fieldKey);
                if (ct !== control && ct.value === control.value) {
                    return { invalid: true };
                }
            });

            return null;
        };
    }

    onTypeChange(i: number) {
        (this.formSaisieTirage.get(`contenants.${i}`) as UntypedFormControl).patchValue({ nombre: '', volume: '' });
    }
}
